import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import './view-portal-accounts.scss';
import { DataTable, Icon, useModal } from '@ampol/design-system';
import {
  getAssignedPortalAccountsList,
  removeAccountAccessForUser,
  removeAccountAccessForUserReset,
} from 'actions/userActions';
import { getAccountLevelName } from 'utilities/core-utilities';
import { setUserDefaultButton } from 'actions/modalMetaActions';
import ContentControls from 'components/content-controls/content-controls';
import { trackCustomEvent } from 'utilities/app-insights';

/**
 * Admin Portal - Users Modal - View Assigned Portal Accounts
 * @returns JSX Element
 */
const ViewPortalAccounts = ({ userId, onClose }) => {
  const dispatch = useDispatch();
  const assignedPortalAccountsListLoading = useSelector((state) => state.userStore.assignedPortalAccountsListLoading);
  const assignedPortalAccountsList = useSelector((state) => state.userStore.assignedPortalAccountsList);
  const assignedPortalAccountsListResultSize = useSelector(
    (state) => state.userStore.assignedPortalAccountsListResultSize
  );

  const removeAccountAccessForUserLoading = useSelector((state) => state.userStore.removeAccountAccessForUserLoading);
  const removeAccountAccessForUserLoaded = useSelector((state) => state.userStore.removeAccountAccessForUserLoaded);
  const removeAccountAccessForUserError = useSelector((state) => state.userStore.removeAccountAccessForUserError);

  const loggedInUser = useSelector((state) => state.userStore.loggedInUser);
  const assignedAccountNumber = useSelector((state) => state.userStore.assignedAccountNumber);

  const [isLastAccount, setIsLastAccount] = useState(false);

  const [sortColumn, setSortColumn] = useState('accountNumber');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedAccountNumber, setSelectedAccountNumber] = useState(null);
  const [searchTerm, setSearchTerm] = useState(null);

  const [pageNumber, setPageNumber] = useState(1);
  const pageSize = 5;
  const offset = (pageNumber - 1) * pageSize;
  const totalPages = Math.ceil(assignedPortalAccountsListResultSize / pageSize);

  useEffect(() => {
    dispatch(setUserDefaultButton());
  }, []);

  useEffect(() => {
    dispatch(getAssignedPortalAccountsList(userId, pageSize, offset, sortColumn, sortDirection, searchTerm));
  }, [pageNumber, sortColumn, sortDirection]);

  useEffect(() => {
    if (!searchTerm) {
      setIsLastAccount(assignedPortalAccountsListResultSize === 1);
    }
  }, [assignedPortalAccountsListResultSize]);

  useEffect(() => {
    if (searchTerm !== null && searchTerm?.length > 2) {
      setPageNumber(1);
      dispatch(getAssignedPortalAccountsList(userId, pageSize, 0, sortColumn, sortDirection, searchTerm));
    }
  }, [searchTerm]);

  useEffect(() => {
    if (removeAccountAccessForUserLoaded) {
      unassignAccountConfirmation.close();
      setSearchTerm(null);

      if (isLastAccount) {
        onClose();
      } else {
        dispatch(getAssignedPortalAccountsList(userId, pageSize, offset, sortColumn, sortDirection));
      }

      dispatch(removeAccountAccessForUserReset());
    }
    if (removeAccountAccessForUserError) {
      unassignAccountConfirmation.close();
    }
  }, [removeAccountAccessForUserLoaded, removeAccountAccessForUserError]);

  const onPageNumberChange = (value) => {
    setPageNumber(value);
    trackCustomEvent(
      'Change Page Number',
      loggedInUser,
      assignedAccountNumber,
      'Clicked to change page of user account assignments list in admin portal.'
    );
  };

  const onTableColumnSorted = (column, isAscending) => {
    setSortColumn(column);
    setSortDirection(isAscending ? 'asc' : 'desc');
  };

  const getGrandParentAccountNumber = () => {
    if (selectedAccountNumber && assignedPortalAccountsList) {
      return assignedPortalAccountsList.find((item) => item.accountNumber === selectedAccountNumber)
        .grandParentAccountNumber;
    }
    return null;
  };

  const unassignAccountConfirmation = useModal({
    id: 'confirm-unassign-account',
    title: `Remove ${isLastAccount ? 'portal' : 'account'} access`,
    content: <>Are you sure you wish to remove access to {isLastAccount ? 'the portal' : 'this account'}?</>,
    color: 'red',
    actions: [
      {
        label: 'Confirm',
        onClick: () => {
          if (removeAccountAccessForUserLoading) {
            return;
          }

          dispatch(
            removeAccountAccessForUser(userId, selectedAccountNumber, getGrandParentAccountNumber(), isLastAccount)
          );

          trackCustomEvent(
            'Deactivate Account or User',
            loggedInUser,
            assignedAccountNumber,
            'Clicked to change page of transfers data.',
            { selectedAccount: selectedAccountNumber, selectedUserId: userId }
          );
        },
        inactive: removeAccountAccessForUserLoading,
      },
      {
        label: 'Cancel',
        onClick: () => {
          if (removeAccountAccessForUserLoading) {
            return;
          }

          unassignAccountConfirmation.close();
        },
        inactive: removeAccountAccessForUserLoading,
      },
    ],
    hideCloseButton: true,
  });

  const onUnassignPortalAccountClick = (accountNumber) => {
    setSelectedAccountNumber(accountNumber);

    unassignAccountConfirmation.open();
  };

  const onSearch = (value) => {
    setSearchTerm(value);
  };

  return (
    <div>
      <ContentControls
        showPageControls={true}
        pageNumber={pageNumber}
        pageTotal={totalPages}
        onPageChange={onPageNumberChange}
        showSearch={true}
        handleSearch={onSearch}
        defaultSearchValue={searchTerm}
        itemCount={assignedPortalAccountsListResultSize}
      />
      <div className={'view-portal-accounts__table-container'}>
        <DataTable
          caption={'Assigned Portal Accounts'}
          columns={[
            {
              header: 'Account Number',
              accessorKey: 'accountNumber',
              defaultSorted: 'asc',
            },
            {
              header: 'Account Name',
              accessorKey: 'accountName',
            },
            {
              header: 'Level',
              id: 'level',
              accessorFn: (x) => getAccountLevelName(x.level),
            },
            {
              header: 'Security Role',
              accessorKey: 'roleName',
            },
            {
              id: 'action',
              accessorFn: (x) => (
                <>
                  <button
                    className={'view-portal-accounts__unassign-button'}
                    onClick={() => onUnassignPortalAccountClick(x.accountNumber)}
                  >
                    <Icon url={'/icons/bin.svg'} />
                  </button>
                </>
              ),
              rtl: true,
            },
          ]}
          isSortable={true}
          onColumnSorted={onTableColumnSorted}
          isLoading={assignedPortalAccountsListLoading}
          columnTemplate={'1fr 2fr 1fr 1fr 70px'}
          data={assignedPortalAccountsList}
          tableVariant='list'
          loadingRowCount={pageSize}
        />
      </div>
    </div>
  );
};

export default ViewPortalAccounts;
